import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Btn, Table, Td, Empty, Badge } from "../components/UI";
import { SlidePanel, ConfirmDialog } from "../components/SlidePanel";
import { FLabel, FInput, FSelect } from "./engagement/FormControls";
import { DateField } from "./engagement/DateField";
import { Plus, Building2, Pencil, Ban, Play, Receipt, Users, Landmark, TrendingUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TENANT_EMPTY = { name: "", contact_name: "", contact_email: "", phone: "", address: "", plan_id: "", license_expires: dayjs().add(1, "year").format("YYYY-MM-DD"), admin: { first_name: "", last_name: "", email: "", password: "" } };

const Stat = ({ icon: Icon, label, value, tone = "bg-[#EFEBE0] text-[#4E342E]" }) => (
  <div className="flex items-center gap-4 rounded-2xl border border-[#EFEBE0] bg-white p-5 flex-1 min-w-[180px]">
    <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}><Icon size={20} /></span>
    <div><div className="font-heading text-2xl font-bold text-[#3E2723]">{value}</div><div className="text-xs text-[#7B6157]">{label}</div></div>
  </div>
);

function TenantsTab({ data, plans, reload }) {
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState(TENANT_EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // {tenant, action}
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const fa = (k) => (e) => setForm((s) => ({ ...s, admin: { ...s.admin, [k]: e.target.value } }));

  const openAdd = () => { setForm({ ...TENANT_EMPTY, plan_id: plans[0]?.id || "" }); setEditId(null); setPanel(true); };
  const openEdit = (t) => {
    setForm({ name: t.name, contact_name: t.contact_name || "", contact_email: t.contact_email || "", phone: t.phone || "",
      address: t.address || "", plan_id: t.plan_id, license_expires: (t.license || {}).expires_at || "", admin: TENANT_EMPTY.admin });
    setEditId(t.id); setPanel(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) {
        const { admin, license_expires, ...rest } = form;
        await api.put(`/platform/tenants/${editId}`, { ...rest, license: { expires_at: license_expires } });
        toast.success("Tenant updated");
      } else {
        await api.post("/platform/tenants", form);
        toast.success(`Tenant "${form.name}" created - admin account ready`);
      }
      setPanel(false); reload();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const doStatus = async () => {
    setBusy(true);
    try {
      await api.put(`/platform/tenants/${confirm.tenant.id}`, { status: confirm.action === "suspend" ? "suspended" : "active" });
      toast.success(`${confirm.tenant.name} ${confirm.action === "suspend" ? "suspended" : "reactivated"}`);
      setConfirm(null); reload();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const genInvoice = async (t) => {
    try {
      await api.post("/platform/invoices", { tenant_id: t.id, period: dayjs().format("YYYY-MM") });
      toast.success(`Invoice generated for ${t.name} (${dayjs().format("MMM YYYY")})`);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <Btn data-testid="platform-add-tenant-btn" onClick={openAdd}><Plus size={16} /> New Tenant</Btn>
      </div>
      <Card>
        <Table headers={["Tenant", "Plan", "Status", "Users", "Clients", "License Expires", "MRR", ""]}>
          {data.tenants.map((t) => {
            const expired = (t.license || {}).expires_at && t.license.expires_at < dayjs().format("YYYY-MM-DD");
            return (
              <tr key={t.id} data-testid={`tenant-row-${t.id}`} className="hover:bg-[#FDFCF8]">
                <Td className="font-semibold"><span className="inline-flex items-center gap-2"><Building2 size={15} className="text-[#7B6157]" /> {t.name}</span></Td>
                <Td><span className="rounded-full bg-[#F5F2EB] px-2.5 py-0.5 text-xs font-semibold capitalize">{t.plan_name}</span></Td>
                <Td><Badge value={t.status} /></Td>
                <Td className="tabular-nums">{t.user_count}{t.max_users ? ` / ${t.max_users}` : ""}</Td>
                <Td className="tabular-nums">{t.client_count}{t.max_clients ? ` / ${t.max_clients}` : ""}</Td>
                <Td className={expired ? "text-[#C62828] font-semibold" : ""}>{(t.license || {}).expires_at || "-"}{expired ? " (expired)" : ""}</Td>
                <Td className="tabular-nums">${t.price_monthly?.toLocaleString()}</Td>
                <Td>
                  <div className="flex gap-1.5 justify-end">
                    <button type="button" data-testid={`tenant-invoice-${t.id}`} title="Generate this month's invoice" onClick={() => genInvoice(t)}
                      className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Receipt size={13} /></button>
                    <button type="button" data-testid={`tenant-edit-${t.id}`} title="Edit tenant" onClick={() => openEdit(t)}
                      className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={13} /></button>
                    {t.status === "active"
                      ? <button type="button" data-testid={`tenant-suspend-${t.id}`} title="Suspend tenant" onClick={() => setConfirm({ tenant: t, action: "suspend" })}
                          className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Ban size={13} /></button>
                      : <button type="button" data-testid={`tenant-activate-${t.id}`} title="Reactivate tenant" onClick={() => setConfirm({ tenant: t, action: "activate" })}
                          className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#2E7D32] flex items-center justify-center hover:bg-[#E8F5E9]"><Play size={13} /></button>}
                  </div>
                </Td>
              </tr>
            );
          })}
        </Table>
        {data.tenants.length === 0 && <Empty text="No tenants yet" />}
      </Card>

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? "Edit Tenant" : "New Tenant (Clinic)"}
        subtitle="Provision an isolated clinic workspace with its own admin, license & plan" width="max-w-xl" testId="tenant-slide-panel">
        <form onSubmit={save} className="space-y-4">
          <div className="rounded-xl border border-[#F1E8DA] bg-[#FDFCF8] p-4 grid grid-cols-2 gap-x-4 gap-y-3">
            <div className="col-span-2"><FLabel>Organization Name *</FLabel><FInput data-testid="tenant-name-input" value={form.name} onChange={f("name")} required /></div>
            <div><FLabel>Contact Name</FLabel><FInput value={form.contact_name} onChange={f("contact_name")} /></div>
            <div><FLabel>Contact Email</FLabel><FInput type="email" value={form.contact_email} onChange={f("contact_email")} /></div>
            <div><FLabel>Phone</FLabel><FInput value={form.phone} onChange={f("phone")} /></div>
            <div><FLabel>Address</FLabel><FInput value={form.address} onChange={f("address")} /></div>
          </div>
          <div className="rounded-xl border border-[#F1E8DA] bg-[#FDFCF8] p-4 grid grid-cols-2 gap-x-4 gap-y-3">
            <div><FLabel>Plan *</FLabel>
              <FSelect data-testid="tenant-plan-select" value={form.plan_id} onChange={f("plan_id")} required>
                {plans.map((p) => <option key={p.id} value={p.id}>{p.name} - ${p.price_monthly}/mo</option>)}
              </FSelect></div>
            <div><FLabel>License Expires *</FLabel><DateField testId="tenant-license-expires" value={form.license_expires} onChange={(v) => setForm((s) => ({ ...s, license_expires: v }))} /></div>
          </div>
          {!editId && (
            <div className="rounded-xl border border-[#F1E8DA] bg-[#FDFCF8] p-4 grid grid-cols-2 gap-x-4 gap-y-3">
              <div className="col-span-2 font-heading text-sm font-bold text-[#3E2723]">Tenant Admin Account</div>
              <div><FLabel>First Name *</FLabel><FInput data-testid="tenant-admin-first" value={form.admin.first_name} onChange={fa("first_name")} required /></div>
              <div><FLabel>Last Name *</FLabel><FInput data-testid="tenant-admin-last" value={form.admin.last_name} onChange={fa("last_name")} required /></div>
              <div><FLabel>Email *</FLabel><FInput data-testid="tenant-admin-email" type="email" value={form.admin.email} onChange={fa("email")} required /></div>
              <div><FLabel>Password *</FLabel><FInput data-testid="tenant-admin-password" type="password" value={form.admin.password} onChange={fa("password")} required minLength={8} /></div>
            </div>
          )}
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn data-testid="tenant-save-btn" type="submit" disabled={busy}>{busy ? "Saving…" : editId ? "Save Changes" : "Create Tenant"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <ConfirmDialog open={!!confirm} onClose={() => setConfirm(null)} onConfirm={doStatus} busy={busy}
        title={confirm?.action === "suspend" ? `Suspend ${confirm?.tenant.name}` : `Reactivate ${confirm?.tenant.name}`}
        confirmWord={confirm?.action === "suspend" ? "SUSPEND" : "ACTIVATE"}
        confirmLabel={confirm?.action === "suspend" ? "Suspend" : "Reactivate"}
        warning={confirm?.action === "suspend"
          ? "All users of this tenant will be locked out immediately. Their data stays intact and you can reactivate anytime."
          : "Users of this tenant will regain access immediately."} />
    </>
  );
}

function PlansTab({ plans, reload }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const save = async () => {
    try {
      await api.put(`/platform/plans/${editing}`, { ...form, price_monthly: Number(form.price_monthly), max_users: Number(form.max_users), max_clients: Number(form.max_clients) });
      toast.success("Plan updated");
      setEditing(null); reload();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      {plans.map((p) => (
        <Card key={p.id} className="p-6" data-testid={`plan-card-${p.key}`}>
          <div className="flex items-start justify-between">
            <h3 className="font-heading text-lg font-bold text-[#3E2723]">{p.name}</h3>
            <button type="button" data-testid={`plan-edit-${p.key}`} aria-label={`Edit ${p.name}`}
              onClick={() => { setEditing(p.id); setForm({ price_monthly: p.price_monthly, max_users: p.max_users, max_clients: p.max_clients }); }}
              className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={13} /></button>
          </div>
          {editing === p.id ? (
            <div className="mt-3 space-y-3">
              <div><FLabel>Monthly Price ($)</FLabel><FInput data-testid={`plan-price-${p.key}`} type="number" value={form.price_monthly} onChange={(e) => setForm((s) => ({ ...s, price_monthly: e.target.value }))} /></div>
              <div><FLabel>Max Users (0 = unlimited)</FLabel><FInput type="number" value={form.max_users} onChange={(e) => setForm((s) => ({ ...s, max_users: e.target.value }))} /></div>
              <div><FLabel>Max Clients (0 = unlimited)</FLabel><FInput type="number" value={form.max_clients} onChange={(e) => setForm((s) => ({ ...s, max_clients: e.target.value }))} /></div>
              <div className="flex gap-2 justify-end">
                <Btn variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn>
                <Btn data-testid={`plan-save-${p.key}`} onClick={save}>Save</Btn>
              </div>
            </div>
          ) : (
            <>
              <div className="mt-2 font-heading text-3xl font-bold text-[#3E2723]">${p.price_monthly?.toLocaleString()}<span className="text-sm font-normal text-[#7B6157]">/mo</span></div>
              <div className="mt-1 text-xs text-[#7B6157]">{p.max_users ? `${p.max_users} users` : "Unlimited users"} · {p.max_clients ? `${p.max_clients} clients` : "Unlimited clients"}</div>
              <ul className="mt-4 space-y-1.5 text-sm text-[#6D4C41]">
                {(p.features || []).map((ft) => <li key={ft} className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#8D6E63] shrink-0" />{ft}</li>)}
              </ul>
            </>
          )}
        </Card>
      ))}
    </div>
  );
}

function InvoicesTab() {
  const [invoices, setInvoices] = useState([]);
  useEffect(() => { api.get("/platform/invoices").then((r) => setInvoices(r.data)); }, []);
  return (
    <Card>
      <Table headers={["Tenant", "Period", "Plan", "Amount", "Status", "Paid At"]}>
        {invoices.map((i) => (
          <tr key={i.id} data-testid={`invoice-row-${i.id}`}>
            <Td className="font-semibold">{i.tenant_name}</Td>
            <Td>{i.period}</Td>
            <Td>{i.plan_name}</Td>
            <Td className="tabular-nums">${i.amount?.toLocaleString()}</Td>
            <Td><Badge value={i.status === "paid" ? "completed" : "pending"} /> <span className="text-xs text-[#7B6157] ml-1">{i.status}</span></Td>
            <Td>{i.paid_at ? dayjs(i.paid_at).format("MM/DD/YYYY") : "-"}</Td>
          </tr>
        ))}
      </Table>
      {invoices.length === 0 && <Empty text="No invoices generated yet - use the receipt icon on a tenant row" />}
    </Card>
  );
}

export default function PlatformConsole() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("tenants");
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const isSuper = !!user?.is_super_admin;
  const reload = () => {
    api.get("/platform/overview").then((r) => setData(r.data)).catch(() => {});
    api.get("/platform/plans").then((r) => setPlans(r.data)).catch(() => {});
  };
  useEffect(() => { if (isSuper) reload(); }, [isSuper]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!isSuper) return (
    <div className="py-24 text-center" data-testid="platform-access-denied">
      <h1 className="font-heading text-xl font-bold text-[#3E2723]">Access restricted</h1>
      <p className="text-sm text-[#7B6157] mt-2">The Platform Console is only available to Cocoa EHR master administrators.</p>
      <Btn className="mt-5" onClick={() => navigate("/")}>Back to Dashboard</Btn>
    </div>
  );
  if (!data) return <div className="py-20 text-center text-[#84675B]">Loading platform…</div>;

  return (
    <div data-testid="platform-console">
      <PageHeader title="Platform Console" subtitle="Cocoa EHR master administration - tenants, licensing & billing" />
      <div className="flex flex-wrap gap-4 mb-6">
        <Stat icon={Landmark} label="Tenants" value={data.totals.tenant_count} />
        <Stat icon={TrendingUp} label="Monthly recurring revenue" value={`$${data.totals.mrr.toLocaleString()}`} tone="bg-[#E8F5E9] text-[#2E7D32]" />
        <Stat icon={Users} label="Total staff users" value={data.totals.total_users} />
        <Stat icon={ClipboardIcon} label="Total clients on platform" value={data.totals.total_clients} />
      </div>
      {data.totals.expiring_soon.length > 0 && (
        <div className="mb-5 flex items-center gap-2 rounded-xl bg-[#FFF8E1] border border-[#FFE082] px-4 py-3 text-sm text-[#8D6E00]" data-testid="expiring-licenses-banner">
          <AlertTriangle size={15} /> Licenses expiring within 30 days: {data.totals.expiring_soon.join(", ")}
        </div>
      )}
      <div className="mb-5 flex gap-2">
        {[["tenants", "Tenants"], ["plans", "Plans & Pricing"], ["invoices", "Invoices"]].map(([k, l]) => (
          <button key={k} data-testid={`platform-tab-${k}`} onClick={() => setTab(k)}
            className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>{l}</button>
        ))}
      </div>
      {tab === "tenants" && <TenantsTab data={data} plans={plans} reload={reload} />}
      {tab === "plans" && <PlansTab plans={plans} reload={reload} />}
      {tab === "invoices" && <InvoicesTab />}
    </div>
  );
}

const ClipboardIcon = (props) => <Users {...props} />;
